/* eslint-disable camelcase */
import { USER_TOURNAMENT_QUANTITATIVE_TASK_TYPES, allCountriesData, sortType } from '../constants';
import allCountries from '../constants/countries.v1.json';
import { Country, DropDownOption, PaymentMethod, PaymentMethodOption } from '../types';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { LIVE_SUBCATEGORIES } from 'src/constants/gameCategories';
import {
  Bonus,
  BonusesResponse,
  LocalTournament,
  LocalTournamentMember,
  makeFloat,
  PaymentFormField
} from 'react-easyrocket';
import { allCountriesForReg } from 'src/constants/constants';

export function checkIfElementPresentsInArray<T>(array: Array<T>, status: T) {
  const colorTextObjectsSet = new Set(array);
  return colorTextObjectsSet.has(status);
}

export const getPhoneCodeByCountryCode = (countryCode: string) => {
  const countyInfo = allCountriesData.find((country) => country.alpha3 === countryCode);
  return countyInfo?.phoneCodes[0];
};

export const findCountryCodeByName = (name: string | undefined) => {
  if (!name) return undefined;
  return allCountries.find((c) => c.name === name)?.alpha3;
};

export const findCountryCodeByNameForReg = (name: string | undefined) => {
  if (!name) return undefined;
  return allCountriesForReg.find((c) => c.name === name)?.alpha3;
};

export const findCountryNameByCode = (code: string) =>
  allCountries.find((c) => c.alpha3 === code)?.name;

export const findCountryNameByCodeForReg = (code: string) =>
  allCountriesForReg.find((c) => c.alpha3 === code)?.name;

export const getArrayOfCountryPhoneCodes = (countries: Country[]) =>
  countries
    .reduce<DropDownOption[]>((result, item) => {
      const code = item.phoneCodes[0];
      if (!code) return result;
      if (result.find((i) => i.name === code)) return result;
      return [...result, { id: item.alpha2, name: code }];
    }, [])
    .sort((a, b) => (a.name > b.name ? 1 : -1));

export const getBonusesByWeekDay = (bonuses: Bonus[]) => {
  const sortedBonuses = [];
  const dayOfWeek = dayjs().day();
  for (let i = 0; i < bonuses?.length; i++) {
    if (bonuses[i].activeDayOfWeek?.length === 0) {
      sortedBonuses.push(bonuses[i]);
    }
    if (bonuses[i].activeDayOfWeek.includes(dayOfWeek)) {
      sortedBonuses.push(bonuses[i]);
    }
  }

  return sortedBonuses;
};

export const prepareVoucherOptions = (options?: PaymentMethodOption[]): DropDownOption[] => {
  return (options || [])
    .sort((currentOption, nextOption) => (currentOption.id || 0) - (nextOption.id || 0))
    .map((el) => ({
      id: el.id?.toString() || '',
      name: `${el.amount} ${el.currency}`
    }));
};

export const validateDepositButton = (
  isFormValid: boolean,
  paymentMethod?: PaymentMethod,
  hasBonusAmountError?: boolean
) => {
  if (hasBonusAmountError) {
    return false;
  }
  return !isFormValid || !paymentMethod;
};

export const splitArrayIntoChunks = <TChunk>(
  chunkSize: number,
  initArray: TChunk[]
): TChunk[][] => {
  const resultArray: TChunk[][] = [];

  for (let i = 0; i < initArray?.length; i += chunkSize) {
    const chunk: TChunk[] = initArray.slice(i, i + chunkSize);
    resultArray.push(chunk);
  }

  return resultArray;
};

const BonusTransactionType: { [key: number]: string } = {
  0: 'arrayTransactions.bonus',
  1: 'arrayTransactions.moneyPrize',
  2: 'arrayTransactions.manual',
  3: 'arrayTransactions.referral',
  4: 'arrayTransactions.freeSpinWin',
  5: 'arrayTransactions.bet',
  6: 'arrayTransactions.betSport',
  7: 'arrayTransactions.win',
  8: 'arrayTransactions.zeroing',
  9: 'arrayTransactions.cancelWager',
  10: 'arrayTransactions.winSport',
  11: 'arrayTransactions.cashback',
  12: 'arrayTransactions.levelBonus',
  13: 'arrayTransactions.refund',
  14: 'arrayTransactions.rollbackBet',
  15: 'arrayTransactions.rollbackWin',
  16: 'bonusType.jackpotWin',
  17: 'arrayTransactions.providerBonus',
  18: 'arrayTransactions.providerJackpot',
  19: 'arrayTransactions.providerPromo'
};

export const setBonusTransactionTypeName = (typeNumber: number) => {
  if (typeNumber in BonusTransactionType) {
    return t(BonusTransactionType[typeNumber]);
  }
  return '';
};

export const findSortById = (id: string) => sortType.find((c) => c.id === id);

export const getLevelInPercents = (pointsToReach?: string | number, userPoints?: number) => {
  if (pointsToReach === undefined || userPoints === undefined || userPoints === 0) return '0%';
  if (pointsToReach === '\u221e') return '100%';
  if (typeof pointsToReach === 'string') {
    return `${(userPoints / parseInt(pointsToReach, 10)) * 100}%`;
  }
  if (typeof pointsToReach === 'number') {
    return `${((userPoints / pointsToReach) * 100).toFixed(2)}%`;
  }
};

export const getLevelInNumber = (pointsToReach?: string | number, userPoints?: number) => {
  if (pointsToReach === undefined || userPoints === undefined || userPoints === 0) return 0;
  if (pointsToReach === '\u221e') return 100;
  if (typeof pointsToReach === 'string') {
    return (userPoints / parseInt(pointsToReach, 10)) * 100;
  }
  if (typeof pointsToReach === 'number') {
    return (userPoints / pointsToReach) * 100;
  }
};

export const prepareMethodOptions = (paymentMethod: PaymentMethod) => {
  if (!paymentMethod) return undefined;
  if (paymentMethod.type === 'coin' && paymentMethod.options) {
    return paymentMethod.options.map((el) => ({
      id: el.name,
      name: el.title
    }));
  } else if (paymentMethod.options) {
    return paymentMethod.options.map((el) => ({
      id: el.id ? el.id.toString() : el.title,
      name: el.title
    }));
  }
  return undefined;
};

export const prepareBonuses = (bonuses: BonusesResponse, depositsCount?: number) => {
  if (depositsCount == undefined) return [];

  const filteredBonuses = bonuses.filter((bonus) => bonus.tag).filter((bonus) => bonus.type === 5);
  const partnerBonuses = bonuses.filter((bonus) => bonus.tag === 'partner');
  const sortedByThreeTagsArray = [];

  for (let i = 0; i < filteredBonuses?.length; i++) {
    if (filteredBonuses[i].tag === 'standard') {
      sortedByThreeTagsArray.push(filteredBonuses[i]);
    }
    if (filteredBonuses[i].tag === 'vip') {
      const standardIndex = sortedByThreeTagsArray.findIndex((el) => el.tag === 'standard');
      if (standardIndex === -1) {
        sortedByThreeTagsArray.push(filteredBonuses[i]);
      } else {
        sortedByThreeTagsArray.splice(standardIndex, 0, filteredBonuses[i]);
      }
    }
    if (filteredBonuses[i].tag === 'special') {
      sortedByThreeTagsArray.unshift(filteredBonuses[i]);
    }
  }

  const sortedByTagsArray = partnerBonuses.concat(sortedByThreeTagsArray);

  return sortedByTagsArray
    .filter(
      (bonus) =>
        depositsCount + 1 >= bonus.numberDeposits.from &&
        (depositsCount + 1 <= bonus.numberDeposits.to || bonus.numberDeposits.to == 0)
    )
    .slice(0, 3);
};

export const openChat = () => {
  // @ts-ignore
  if (typeof window.LiveChatWidget !== undefined) {
    // @ts-ignore
    window.LiveChatWidget?.call?.('maximize');
  }
};

// export const containsLanguage = (arrayLanguages: LangOptions, route: string) => {
//   // eslint-disable-next-line no-useless-escape
//   const languageRegExp = new RegExp(`\/(${arrayLanguages.join('|')})(\/|$)`);
//   return route.match(languageRegExp);
// };

export const executeAdditionalGTMScript = (userId: number | undefined) => {
  (function () {
    // @ts-ignore
    window.dataLayer = window.dataLayer || [];
    // @ts-ignore
    window.dataLayer.push({
      event: 'userId',
      user_id: userId ? userId.toString() : userId
    });
  })();
};

export const executeRegistrationGTMScript = (userId: number) => {
  executeAdditionalGTMScript(userId);
  // @ts-ignore
  window.dataLayer = window.dataLayer || [];
  // @ts-ignore
  window.dataLayer.push({
    event: 'registration',
    user_id: userId.toString()
  });
};

export const executeLoginGTMScript = (userId: number) => {
  executeAdditionalGTMScript(userId);
  // @ts-ignore
  window.dataLayer = window.dataLayer || [];
  // @ts-ignore
  window.dataLayer.push({
    event: 'login',
    user_id: userId.toString()
  });
};

export const getLevelsString = (from?: number, to?: number): string => {
  if (from === undefined || to === undefined) {
    return '';
  }
  return `${from} exp. to Level ${to}`;
};

export const getSummOfPossiblyUndefinedNums = (first?: number, second?: number) => {
  if (first && second) {
    return first + second;
  } else if (!first && second) {
    return second;
  } else if (first && !second) {
    return first;
  }
  return 0;
};

export const processContentTables = (content?: string) => {
  if (!content) return '';
  return content
    ?.replace(/<table/g, '<div class=scrollable-table-wrapper><table')
    .replace(/<\/table>/g, '</table></div>');
};

export const getIsActiveTournament = (tournament?: LocalTournament) => {
  if (!tournament) return false;
  return Boolean(tournament.dateEnd && Date.now() < tournament.dateEnd * 1000);
};

export function checkForLiveSubcategory(locationPathname: string) {
  for (let i = 0; i < LIVE_SUBCATEGORIES?.length; i++) {
    if (locationPathname.includes(LIVE_SUBCATEGORIES[i])) {
      return true;
    }
  }
  return false;
}

export const tournamentProgressFormatter = (
  progress: LocalTournamentMember['progress'],
  taskType?: number,
  type?: number
) => {
  const userProgress = typeof progress === 'number' ? progress : progress?.[0]?.progress;

  if (type === 0) {
    return `${userProgress}%`;
  }

  if (USER_TOURNAMENT_QUANTITATIVE_TASK_TYPES.some((type) => type === taskType)) {
    return makeFloat(userProgress);
  }

  return userProgress;
};

export function generateSelectOptionsForFormGenerator(
  field: PaymentFormField & { options?: { [key: string]: string } }
): [string, string][] {
  if (field.options) {
    return Object.entries(field.options);
  }
  return Object.entries(field.value);
}

export function getUserIdFromLS(): number | undefined {
  const userId = localStorage.getItem('userId');
  return userId ? parseInt(userId) : undefined;
}

export function getLanguageFromLS() {
  return localStorage.getItem('language');
}
